import { Patient } from '../../../models/patient';
import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { PatientService } from '../../../services/patient.service';
import { combineLatest, defaultIfEmpty, distinctUntilChanged, finalize, firstValueFrom, Observable, startWith, Subject, takeUntil } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { AuxiliarService } from '../../../services/auxiliar.service';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { DocSignatureType } from '../../../models/document-signature-type';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { dateAfterValidator } from '../../../utils/validators/dateAfterValidator';
import moment from 'moment';
import { EmployeeService } from '../../../services/employee.service';
import { Employee } from '../../../models/employee';
import { TenantService } from '../../../services/tenant.service';
import { Tenant } from '../../../models/tenant';

@Component({
  selector: 'app-doc-sign-drawer',
  standalone: false,
  templateUrl: './doc-sign-drawer.component.html',
  styleUrl: './doc-sign-drawer.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class DocSignDrawerComponent implements OnInit, OnDestroy {
  @Input() docType: DocSignatureType;
  @Input() patient: Patient;

  public activeSignature: string;
  public attendanceOptions: {name: string, value: boolean}[] = [];
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public form: FormGroup;
  public loadingDoc: boolean;
  public pdfUrl: SafeResourceUrl;
  public professionals: Employee[] = [];
  public signatureA: any;
  public signatureB: any;
  private tenant: Tenant;

  get f(): { [index: string]: any } {
    return this.form.controls;
  }

  constructor(
    private patientService: PatientService,
    private logger: NGXLogger,
    private auxiliarService: AuxiliarService,
    private translate: TranslateService,
    private formBuilder: FormBuilder,
    private sanitizer: DomSanitizer,
    private employeeService: EmployeeService,
    private tenantService: TenantService,
  ){}

  async ngOnInit() {
    if(this.docType.signatures) this.activeSignature = 'A';
    if(this.docType.url) this.getPdfFile();
    this.attendanceOptions = this.auxiliarService.getBinaryOptions();
    this.tenant = this.tenantService.getAppTenant();
    this.professionals = await this.getProfessionals();
    this.loadForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private getPdfFile(): void {
    this.auxiliarService.startSpinnerReq();
    this.patientService.getPatientDoc(this.docType).pipe(
      finalize(() => this.auxiliarService.stopSpinnerReq()),
      takeUntil(this.destroy$)
    ).subscribe({
      next: async (res: any) => {
        this.logger.debug('[patientService] getPatientDoc res: ', res);
        const blob = new Blob([res.body], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      },
      error: (err: any) => {
        this.logger.error('[patientService] getPatientDoc err:', err);
      }
    });
  }

  private async getProfessionals(): Promise<Employee[]> {
    try {
      const res: any = await firstValueFrom(
        this.employeeService.getScheduleEmployees(this.tenant).pipe(
          defaultIfEmpty(null)
        )
      );
      this.logger.debug('[employeeService] getScheduleEmployees (professionals) res: ', res);
      return _.orderBy(_.filter(_.get(res, 'body.data', []), { isProfessional: true }), 'givenname');
    } catch (err) {
      // cancellation, not error
      if (_.get(err, 'name') === 'ObjectUnsubscribedError') {
        this.logger.error('[employeeService] getScheduleEmployees cancelled');
        return [];
      }
      // error
      this.logger.error('[employeeService] getScheduleEmployees (professionals) err:', err);
      this.auxiliarService.toast(this.translate.instant('error.professionals'), 'error');
      return [];
    }
  }

  private loadForm(): void {
    this.form = this.formBuilder.group({
      attendance: [false, []],
      attendanceComment: ['', []],
      attendanceName: ['', []],
      attendanceTaxcode: ['', []],
      begin: ['', []],
      description: [this.docType ? 
        this.translate.instant(`doc-signature-types.${this.docType.id}-desc`) : '', [Validators.required]],
      docType: [this.docType, [Validators.required]],
      end: ['', []],
      files: [[], []],
      name: [this.docType ? 
        this.translate.instant(`doc-signature-types.${this.docType.id}-name`) : '', [Validators.required]],
      patient: [this.patient, [Validators.required]],
      patientComments: ['', []],
      patientName: [{value: this.patient?.givenname, disabled: true}, []],
      patientTaxcode: [{value: this.patient?.taxcode, disabled: true}, []],
      professional: ['', []],
      professionalTaxcode: [{value: '', disabled: true}, []],
    });

    if(this.docType.signatures === 1) {
      this.f['files'].setValidators([Validators.minLength(1)]);
    }

    if(this.docType.signatures === 2) {
      this.f['files'].setValidators([Validators.minLength(2)]);
      this.f['professional'].setValidators([Validators.required]);
      this.f['professionalTaxcode'].setValidators([Validators.required]);
    }

    if(this.docType.dates) {
      this.f['begin'].setValidators([Validators.required]);
      this.f['end'].setValidators([Validators.required, dateAfterValidator(this.f['begin'])]);
    }

    // professional value change
    this.f['professional'].valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: Employee) => {
        this.f['professionalTaxcode'].setValue(value?.professionalCode || '');
      });

    // attendance value change
    this.f['attendance'].valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: boolean) => {
        if(value) {
          this.f['attendanceName'].setValidators([Validators.required]);
          this.f['attendanceTaxcode'].setValidators([Validators.required]);
        } else {
          this.f['attendanceName'].setValidators([]);
          this.f['attendanceTaxcode'].setValidators([]);
          this.f['attendanceName'].setValue('');
          this.f['attendanceTaxcode'].setValue('');
        }
        this.f['attendanceName'].updateValueAndValidity();
        this.f['attendanceTaxcode'].updateValueAndValidity();
      });

    // firstname / lastname value change to autogenerate username if it has not been modified by hand by user
    combineLatest({
      begin: this.f['begin'].valueChanges.pipe(startWith(this.f['begin'].value)) as Observable<string>,
      end: this.f['end'].valueChanges.pipe(startWith(this.f['end'].value)) as Observable<string>,
    }).subscribe(({ begin, end }) => {
      if (begin && end && this.f['description'].value === this.translate.instant(`doc-signature-types.${this.docType.id}-desc`)) {
        const description = this.translate.instant(`doc-signature-types.${this.docType.id}`) +
          ` (${moment(begin).format('DD/MM/YYYY')} - ${moment(end).format('DD/MM/YYYY')})`;
        this.f['description'].setValue(description, { emitEvent: false });
      }
    });
  }

  public saveSignatureA($event: any): void {
    this.signatureA = $event;
    if(this.signatureA && this.docType.signatures === 2) {
      this.activeSignature = 'B';
    } else if(this.signatureA) {
      this.activeSignature = '';
    }
  }

  public saveSignatureB($event: any): void {
    this.signatureB = $event;
    if(this.signatureB) this.activeSignature = '';
  }

  async sign() {
    this.form.markAllAsTouched();
    if(this.activeSignature || !this.form.valid) return;
    // generate signature A png
    if(this.signatureA) {
      const responseA = await fetch(this.signatureA);
      const blobA = await responseA.blob();
      const fileA = new File([blobA], 'image0.png', { type: 'image/png' });
      this.f['files'].value.push(fileA);
    }
    // generate signature B png
    if(this.signatureB) {
      const responseB = await fetch(this.signatureB);
      const blobB = await responseB.blob();
      const fileB = new File([blobB], 'image0.png', { type: 'image/png' });
      this.f['files'].value.push(fileB);
    }
    // format form
    const formData = _.cloneDeep(this.form.getRawValue());
    if(formData.begin) {
      _.set(formData, 'begin', moment(formData.begin).format('YYYY-MM-DD'));
    }
    if(formData.end) {
      _.set(formData, 'end', moment(formData.end).format('YYYY-MM-DD'));
    }
    if(formData.professional) {
      _.set(formData, 'professionalName', formData.professional.givenname);
      delete formData.professional;
    }
    // generate doc
    this.auxiliarService.startSpinnerReq();
    this.patientService.signPatientDoc(formData).pipe(
      finalize(() => this.auxiliarService.stopSpinnerReq()),
      takeUntil(this.destroy$)
    ).subscribe({
      next: async (res: any) => {
        this.logger.debug('[patientService] signPatientDoc res: ', res);
        this.auxiliarService.closeDrawer('right', _.get(res, 'body'));
      },
      error: (err: any) => {
        this.logger.error('[patientService] signPatientDoc err:', err);
        this.auxiliarService.toast(this.translate.instant('error.patient-doc-sign'), 'error');
      }
    });
  }

  public cancelSign(): void {
    this.auxiliarService.closeDrawer();
  }
}